import React, { useMemo } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { REFERRAL_SOURCES } from '../constants';

interface ReferralSourceSelectProps {
  value: string;
  onChange: (value: string) => void;
  showError?: boolean;
}

// Helper to parse the stored value into dropdown selection and custom text
const parseReferralValue = (
  value: string,
): { selectedOption: string; customText: string } => {
  // Check if the value starts with "Other: " or "Referral Firm - Other: "
  if (value.startsWith('Other: ')) {
    return { selectedOption: 'Other', customText: value.slice(7) };
  }
  if (value.startsWith('Referral Firm - Other: ')) {
    return {
      selectedOption: 'Referral Firm - Other',
      customText: value.slice(23),
    };
  }
  // Otherwise it's a standard selection
  return { selectedOption: value, customText: '' };
};

// Helper to combine dropdown selection and custom text into stored value
const combineReferralValue = (
  selectedOption: string,
  customText: string,
): string => {
  if (selectedOption === 'Other' && customText.trim()) {
    return `Other: ${customText}`;
  }
  if (selectedOption === 'Referral Firm - Other' && customText.trim()) {
    return `Referral Firm - Other: ${customText}`;
  }
  return selectedOption;
};

export const ReferralSourceSelect: React.FC<ReferralSourceSelectProps> = ({
  value,
  onChange,
  showError = false,
}) => {
  const isEmpty = !value || value.trim() === '';
  const shouldShowError = showError && isEmpty;
  // Group sources by their group property
  const directSources = REFERRAL_SOURCES.filter((s) => s.group === 'Direct');
  const referralFirmSources = REFERRAL_SOURCES.filter(
    (s) => s.group === 'Referral Firm',
  );

  // Parse current value
  const { selectedOption, customText } = useMemo(
    () => parseReferralValue(value),
    [value],
  );

  // Check if "Other" is selected (either Direct or Referral Firm)
  const isOtherSelected =
    selectedOption === 'Other' || selectedOption === 'Referral Firm - Other';

  const handleDropdownChange = (newSelection: string) => {
    // When changing dropdown, if switching to a non-Other option, clear custom text
    if (newSelection !== 'Other' && newSelection !== 'Referral Firm - Other') {
      onChange(newSelection);
    } else {
      // Switching to an "Other" option, preserve any existing custom text
      onChange(combineReferralValue(newSelection, customText));
    }
  };

  const handleCustomTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(combineReferralValue(selectedOption, e.target.value));
  };

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <Label
          htmlFor="referral-source"
          className={`text-sm font-medium ${shouldShowError ? 'text-red-700' : ''}`}
        >
          Referral Source {shouldShowError && <span className="text-red-600">*</span>}
        </Label>
        <Select value={selectedOption} onValueChange={handleDropdownChange}>
          <SelectTrigger
            id="referral-source"
            className={`w-full text-sm ${shouldShowError ? 'border-red-500 focus:ring-red-500' : ''}`}
          >
            <SelectValue placeholder="Select referral source" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel className="text-xs font-semibold text-gray-500">
                Direct Sources
              </SelectLabel>
              {directSources.map((source) => (
                <SelectItem
                  key={source.value}
                  value={source.value}
                  className="text-sm"
                >
                  {source.label}
                </SelectItem>
              ))}
            </SelectGroup>
            <SelectGroup>
              <SelectLabel className="text-xs font-semibold text-gray-500">
                Referral Firm
              </SelectLabel>
              {referralFirmSources.map((source) => (
                <SelectItem
                  key={source.value}
                  value={source.value}
                  className="text-sm"
                >
                  {source.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {isOtherSelected && (
        <div className="space-y-1">
          <Label
            htmlFor="referral-source-other"
            className="text-sm font-medium"
          >
            {selectedOption === 'Referral Firm - Other'
              ? 'Referral Firm Name'
              : 'Other Source Details'}
          </Label>
          <Input
            id="referral-source-other"
            type="text"
            placeholder={
              selectedOption === 'Referral Firm - Other'
                ? 'Enter the referral firm name'
                : 'Enter the referral source details'
            }
            value={customText}
            onChange={handleCustomTextChange}
            className="text-sm"
          />
        </div>
      )}
      {shouldShowError && (
        <p className="text-xs text-red-600 mt-1 font-medium">
          Referral Source is required to create a matter
        </p>
      )}
    </div>
  );
};

export default ReferralSourceSelect;
